import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';

import { Box, Divider, Tag, Text, TextButton } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { EditListingTourConfigModal, EditListingTourConfigSteps } from '@zg-rentals/touring';
import { FeeType, ForRentByType, PhoneVerificationSource } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyEditWebApiHandler_phone } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useTourSchedulingContext } from '../../../../../contexts/TourSchedulingContext';
import {
  getListingDetailsQueryKey,
  useGetListingDetails,
  useGetListingRecommendations,
} from '../../../../../hooks/queries/properties';
import accessUtils from '../../../../../utils/accessUtils';
import numberUtils from '../../../../../utils/numberUtils';
import propertyUtils from '../../../../../utils/propertyUtils';
import typeCheckUtils from '../../../../../utils/typeCheckUtils';
import { getIsInputFormDisabled } from '../../../../MFAccordion/utils';

import { REVIEW_PAGE_SECTIONS } from '../../../../../constants/ListingDetailConstants';
import { RECS } from '../../../../../constants/RecommendationsConstants';
import { DEFAULT_VERIFICATION_CONFIG } from '../../../../modals/VerificationModal/constants';
import { FOR_RENT_BY, LEASE_DURATION } from '../constants';
import { SF_MODAL_COMPONENTS } from '../constants';

import ApplicationSampleModal from '../../../../modals/ApplicationSampleModal';
import RecommendationModal from '../../../../modals/RecommendationModal';
import { VerificationModal } from '../../../../modals/VerificationModal';
import AccordionIconError from '../components/AccordionIconError';
import ContentSectionEditButton from '../components/ContentSectionEditButton';
import FieldValueDisplay from '../components/FieldValueDisplay';

import { iconPlaceholderStyles, reviewSectionStyles, reviewSectionTitleStyles, reviewTitleRowStyles } from '../styles';

import { emitClickSampleApplicationLinkOnPageEvent, EVENT_IDS } from '../../../../../analytics/events/modularListing';
import {
  emitReviewStepBookingMethodImpressionEvent,
  emitReviewStepEditAvailabilityClickEvent,
  emitReviewStepEditAvailabilityImpressionEvent,
  emitReviewStepEditBookingMethodClickEvent,
  emitReviewStepTourDurationClickEvent,
  EVENT_IDS as TOURING_EVENT_IDS,
} from '../../../../../analytics/events/touringModularListing';
import { BROKER_LICENSE_MAP } from '../../../../AgentLicenseType';
import { useAuth } from '../../../../AuthProvider';
import ContactEmailMismatchAlert from '../../../../ContactEmailMismatchAlert';
import ContactEmailDeliverabilityAlert from '../../../../PostingPath/ContactEmailDeliverabilityAlert';
import PhoneNumberDisplayInfo from '../../../../PostingPath/PhoneNumberDisplayInfo';
import RecommendationIconBulb from '../../../../PostingPath/RecommendationIconBulb';
import TouringSection from './TouringSection';

import type { MouseEvent, MutableRefObject } from 'react';

const ariaLabels = {
  CONTACT_BY_PHONE: 'contact-by-phone',
  CONTACT_INFO_EMAIL: 'contact-info-email',
  CONTACT_INFO_NAME: 'contact-info-name',
  CONTACT_INFO_PHONE: 'contact-info-phone',
  DATE_AVAILABLE: 'date-available',
  FOR_RENT_BY: 'for-rent-by',
  LEASE_LENGTH: 'lease-length',
  LEASE_TERMS: 'lease-terms',
  LISTED_BY: 'listed-by',
  RENTERS_INSURANCE: 'renters-insurance',
  ZILLOW_APPLICATIONS: 'zillow-applications',
};

type Props = {
  onEditClick: (event: MouseEvent<HTMLButtonElement>) => void;
  openTourModalRef?: MutableRefObject<(() => void) | null>;
};

const FinalDetailsContent = ({ onEditClick, openTourModalRef }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsData, recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: false,
  });

  const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({
    canBeRelisted: listingDetails?.canBeRelisted ?? false,
  });

  const canAccessSFCostsAndFees = accessUtils.canAccessSFCostsAndFees({
    isFeed: listingDetails?.isFeed ?? false,
    isMultifamily: listingDetails?.isMultifamily ?? false,
  });

  const canAccessMLReactivationFlow = accessUtils.canAccessMLReactivationFlow({
    lastActivatedMs: listingDetails?.lastActivatedMs,
    isFeed: listingDetails?.isFeed,
    isMultifamily: listingDetails?.isMultifamily,
  });

  const canAccessPostingPathTourScheduling = accessUtils.isTourSchedulingEnabledForProperty({
    active: listingDetails?.active ?? false,
    canBeRelisted: listingDetails?.canBeRelisted ?? false,
    isFeed: listingDetails?.isFeed ?? false,
    isInReactivationFlow: canAccessMLReactivationFlow,
  });

  const [recommendationType, setRecommendationType] = useState('');
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const [displaySampleModal, setDisplaySampleModal] = useState(false);
  const [touringModalStep, setTouringModalStep] = useState<EditListingTourConfigSteps>(
    EditListingTourConfigSteps.SET_BOOKING_METHOD,
  );
  const [displayTourBookingModal, setDisplayTourBookingModal] = useState(false);

  const { tourCalendarApiHook } = useTourSchedulingContext();

  const { mutateAsync: editPhoneNumber } = useRentalManagerUserPropertyEditWebApiHandler_phone({});

  const { data: applicationFee = [] } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType(
    {
      feeType: FeeType.APPLICATION_FEE,
      listingAlias,
    },
    {
      meta: { tags: ['sf-applicationFee'] },
      enabled: canAccessSFCostsAndFees && listingAlias !== '',
    },
  );

  const contactEmail = listingDetails?.email ?? '';
  const contactName = listingDetails?.contactName ?? '';
  const contactPhone = listingDetails?.phone ?? '';
  const forRentByType = listingDetails?.forRentByType ?? '';
  const leaseDuration = listingDetails?.leaseDuration ?? '';
  const leaseTerms = listingDetails?.leaseTerms ?? '';
  const isApplicationsAccepted = listingDetails?.isApplicationsAccepted;
  const isApplicationsUpdateRestricted = listingDetails?.restrictions?.applications?.isUpdateRestricted ?? false;
  const isApplicationsAccessRestricted = listingDetails?.restrictions?.applications?.isAccessRestricted ?? true;
  const applicationsOwnerEmail = listingDetails?.restrictions?.applications?.ownerEmail ?? '';
  const isHidePhoneRestricted = listingDetails?.restrictions?.isHidePhoneRestricted ?? false;
  const isRentersInsuranceRequired = listingDetails?.rentersInsurance?.isRentersInsuranceRequired;
  const isFeed = listingDetails?.isFeed ?? false;

  const feeAmount = applicationFee?.length ? applicationFee?.[0]?.feeAmountMin : null;
  const customNoneFieldValueText = isFeed ? 'None' : 'Action needed';
  const isDisabled = getIsInputFormDisabled(listingDetails, user?.features?.canUpdateListing ?? false);
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });

  const displayContactPhone = numberUtils.formatPhoneNumberInput(contactPhone);
  const displayLeaseDuration = LEASE_DURATION[leaseDuration];
  const displayRentersInsurance = typeCheckUtils.booleanToLabel(isRentersInsuranceRequired, 'Required', 'Not required');

  const displayListingExclusive = listingDetails?.agent?.isExclusive ? 'Yes' : 'No';

  const floorplan = listingDetails?.floorPlans?.[0];
  const pDateAvailable = floorplan?.dateAvailable ?? '';
  // after format the day is behind by 1 day
  const dateAvailable = new Date(pDateAvailable);

  const displayDateAvailable =
    pDateAvailable &&
    isValid(dateAvailable) &&
    format(new Date(dateAvailable.getTime() - dateAvailable.getTimezoneOffset() * -60000), 'MM/dd/yyyy');

  const isForRentByTenantAndMissingPermissionToCreate =
    isRoomForRent &&
    forRentByType === ForRentByType.TENANT &&
    !listingDetails?.roomForRent?.hasPermissionToCreateListing;

  const recommendations = recommendationsBySection[REVIEW_PAGE_SECTIONS.CONTACT] ?? {};
  const recommendationTypes = new Set(recommendationsData?.recommendations.map((r) => r.type) ?? []);

  const handlePhoneNumberAdded = async (cleanPhoneNumber: string) => {
    await editPhoneNumber({ getParams: { phone: cleanPhoneNumber, listingAlias } });
    await queryClient.refetchQueries({ queryKey: getListingDetailsQueryKey(listingAlias) });
  };

  const handleVerificationConfirmation = async (phoneNumber: string) => {
    await editPhoneNumber({ getParams: { phone: phoneNumber, listingAlias } });
    await queryClient.refetchQueries({ queryKey: getListingDetailsQueryKey(listingAlias) });
  };

  const handleEditPhoneClick = (event: MouseEvent<HTMLButtonElement>) => {
    setShowPhoneVerificationModal(true);
    onEditClick(event);
  };

  const trackTouringModalImpression = useCallback(
    (step: EditListingTourConfigSteps) => {
      if (!listingDetails || !user) {
        return;
      }

      if (
        step === EditListingTourConfigSteps.SET_BOOKING_METHOD ||
        step === EditListingTourConfigSteps.EDIT_BOOKING_METHOD
      ) {
        return emitReviewStepBookingMethodImpressionEvent({
          currentProperty: listingDetails,
          user,
          isActive: listingDetails.active ?? false,
        });
      }
      if (step === EditListingTourConfigSteps.EDIT_AVAILABILITY) {
        return emitReviewStepEditAvailabilityImpressionEvent({
          currentProperty: listingDetails,
          user,
          isActive: listingDetails.active ?? false,
        });
      }
    },
    [listingDetails, user],
  );

  const handleTouringModalStepChange = (step: EditListingTourConfigSteps) => {
    trackTouringModalImpression(step);
  };

  useEffect(() => {
    if (displayTourBookingModal) {
      trackTouringModalImpression(touringModalStep);
    }
  }, [displayTourBookingModal, touringModalStep, trackTouringModalImpression]);

  const handleEditTourClick = (modalStep: EditListingTourConfigSteps) => {
    if (!listingDetails || !user) {
      return;
    }

    if (
      modalStep === EditListingTourConfigSteps.EDIT_BOOKING_METHOD ||
      modalStep === EditListingTourConfigSteps.SET_BOOKING_METHOD
    ) {
      emitReviewStepEditBookingMethodClickEvent({
        currentProperty: listingDetails,
        user,
        isActive: listingDetails.active ?? false,
      });
    } else if (modalStep === EditListingTourConfigSteps.EDIT_AVAILABILITY) {
      emitReviewStepEditAvailabilityClickEvent({
        currentProperty: listingDetails,
        user,
        isActive: listingDetails.active ?? false,
      });
    }

    setTouringModalStep(modalStep);
    setDisplayTourBookingModal(true);
  };

  const handleEditTourClickRef = useRef(handleEditTourClick);
  handleEditTourClickRef.current = handleEditTourClick;

  useEffect(() => {
    if (openTourModalRef) {
      openTourModalRef.current = () => handleEditTourClickRef.current(EditListingTourConfigSteps.EDIT_AVAILABILITY);
      return () => {
        openTourModalRef.current = null;
      };
    }
  }, [openTourModalRef]);

  const handleTourDurationSave = useCallback(() => {
    if (!listingDetails || !user) {
      return;
    }
    emitReviewStepTourDurationClickEvent({
      currentProperty: listingDetails,
      user,
      isActive: listingDetails.active ?? false,
      action: 'save',
    });
  }, [listingDetails, user]);

  const handleTourDurationCancel = useCallback(() => {
    if (!listingDetails || !user) {
      return;
    }
    emitReviewStepTourDurationClickEvent({
      currentProperty: listingDetails,
      user,
      isActive: listingDetails.active ?? false,
      action: 'cancel',
    });
  }, [listingDetails, user]);

  return (
    <div data-testid="ContactContent" data-group-title="Contact">
      <Box css={reviewSectionStyles} data-testid="DateAvailable">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {recommendations[RECS.DATE_AVAILABLE] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.DATE_AVAILABLE}
                  removeFlex={true}
                  onClick={() => {
                    setRecommendationType(RECS.DATE_AVAILABLE);
                  }}
                />
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Text textStyle="body-bold">Date available</Text>
              {recommendations[RECS.DATE_AVAILABLE] && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.DATE_AVAILABLE}`}
                  textStyle="body"
                  onClick={() => {
                    setRecommendationType(RECS.DATE_AVAILABLE);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.DATE_AVAILABLE}
            value={SF_MODAL_COMPONENTS.DateAvailableFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={displayDateAvailable || ''} />
      </Box>

      <Box css={reviewSectionStyles} data-testid="LeaseTerms">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {recommendations[RECS.LEASE_TERMS] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.LEASE_TERMS}
                  removeFlex={true}
                  onClick={() => {
                    setRecommendationType(RECS.LEASE_TERMS);
                  }}
                />
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Text textStyle="body-bold">Lease terms</Text>
              {recommendationTypes.has(RECS.LEASE_TERMS) && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.LEASE_TERMS}`}
                  textStyle="body"
                  onClick={() => {
                    setRecommendationType(RECS.LEASE_TERMS);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.LEASE_TERMS}
            value={SF_MODAL_COMPONENTS.LeaseTermsFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={leaseTerms} />
      </Box>

      {listingDetails?.rentersInsurance?.isEligibleForIsRentersInsuranceRequiredQuestion && (
        <Box css={reviewSectionStyles} data-testid="RentersInsurance">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles} />
              <Text textStyle="body-bold">Renters insurance</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.RENTERS_INSURANCE}
              value={SF_MODAL_COMPONENTS.RentersInsuranceFormField.componentKey}
              onEditClick={onEditClick}
            />
          </Box>
          <FieldValueDisplay value={displayRentersInsurance} />
        </Box>
      )}

      <Box css={reviewSectionStyles} data-testid="LeaseDuration">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {!leaseDuration && !isDisabled && !recommendations[RECS.LEASE_LENGTH] && (
                <AccordionIconError tabIndex={0} title="Lease duration incomplete error" marginRight="xs" />
              )}
              {recommendations[RECS.LEASE_LENGTH] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.LEASE_LENGTH}
                  removeFlex={true}
                  onClick={() => {
                    setRecommendationType(RECS.LEASE_LENGTH);
                  }}
                />
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Text textStyle="body-bold">Lease duration</Text>
              {recommendationTypes.has(RECS.LEASE_LENGTH) && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.LEASE_LENGTH}`}
                  textStyle="body"
                  onClick={() => {
                    setRecommendationType(RECS.LEASE_LENGTH);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.LEASE_LENGTH}
            value={SF_MODAL_COMPONENTS.LeaseDurationFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>
        <FieldValueDisplay value={displayLeaseDuration} />
      </Box>

      <Divider />
      <Box css={{ marginBottom: '1000' }} />

      {isRoomForRent ? (
        <Box css={reviewSectionStyles} data-testid="Contact">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles}>
                {(!forRentByType || isForRentByTenantAndMissingPermissionToCreate) && !isDisabled && (
                  <AccordionIconError tabIndex={0} title="Listed by incomplete error" marginRight="xs" />
                )}
              </Box>
              <Text textStyle="body-bold">Listed by</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.LISTED_BY}
              value={SF_MODAL_COMPONENTS.RoomForRentByFormField.componentKey}
              onEditClick={onEditClick}
            />
          </Box>

          <FieldValueDisplay value={FOR_RENT_BY[listingDetails?.forRentBy ?? '']} />
        </Box>
      ) : (
        <Box css={reviewSectionStyles} data-testid="Contact">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles}>
                {!forRentByType && !isDisabled && (
                  <AccordionIconError tabIndex={0} title="Listed by incomplete error" marginRight="xs" />
                )}
              </Box>
              <Text textStyle="body-bold">Listed by</Text>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.FOR_RENT_BY}
              value={SF_MODAL_COMPONENTS.ForRentByFieldSet.componentKey}
              onEditClick={onEditClick}
            />
          </Box>

          <FieldValueDisplay
            value={FOR_RENT_BY[listingDetails?.forRentBy ?? '']}
            customNoneText={customNoneFieldValueText}
          />
        </Box>
      )}

      {!listingDetails?.restrictions?.isUpdateAgentRestricted && forRentByType === ForRentByType.AGENT && (
        <Box css={reviewSectionStyles} data-testid="AdditionalBrokerInformation">
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles} />
            <Text textStyle="body-bold">Listing is exclusive</Text>
          </Box>

          <FieldValueDisplay value={displayListingExclusive} />
        </Box>
      )}

      {forRentByType === ForRentByType.AGENT &&
        !listingDetails?.restrictions?.isUpdateAgentRestricted &&
        listingDetails?.agent?.licenseType && (
          <Box css={reviewSectionStyles} data-testid="AgentLicenseType">
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles} />
              <Text textStyle="body-bold">License type</Text>
            </Box>

            <FieldValueDisplay value={BROKER_LICENSE_MAP[listingDetails?.agent?.licenseType]} />
          </Box>
        )}

      <Box css={reviewSectionStyles} data-testid="Name">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {!contactName && !isDisabled && !isFeed && (
                <AccordionIconError tabIndex={0} title="Name incomplete error" marginRight="xs" />
              )}
              {recommendations[RECS.CONTACT] && (
                <RecommendationIconBulb
                  fieldName={ariaLabels.CONTACT_INFO_NAME}
                  removeFlex={true}
                  onClick={() => {
                    setRecommendationType(RECS.CONTACT);
                  }}
                />
              )}
            </Box>
            <Box css={reviewSectionTitleStyles}>
              <Text textStyle="body-bold">Name</Text>
              {recommendations[RECS.CONTACT] && (
                <TextButton
                  aria-label={`learn-more-about-${ariaLabels.CONTACT_INFO_NAME}`}
                  textStyle="body"
                  onClick={() => {
                    setRecommendationType(RECS.CONTACT);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </Box>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.CONTACT_INFO_NAME}
            value={SF_MODAL_COMPONENTS.NameFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>

        <FieldValueDisplay value={contactName} className="fs-mask" />
      </Box>

      <Box css={reviewSectionStyles} data-testid="Email">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {!contactEmail && !isDisabled && (
                <AccordionIconError tabIndex={0} title="Email incomplete error" marginRight="xs" />
              )}
            </Box>
            <Text textStyle="body-bold">Email</Text>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.CONTACT_INFO_EMAIL}
            value={SF_MODAL_COMPONENTS.EmailFormField.componentKey}
            onEditClick={onEditClick}
          />
        </Box>

        <FieldValueDisplay value={contactEmail} className="fs-mask" />
        {contactEmail && !isDisabled && (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles} />
            <Box css={{ flex: 1 }}>
              <Box css={{ marginBlock: '400' }}>
                <ContactEmailMismatchAlert contactEmail={contactEmail} />
              </Box>

              {user?.features?.canViewContactEmailDeliverability && (
                <Box css={{ marginTop: '400' }}>
                  <ContactEmailDeliverabilityAlert />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box css={reviewSectionStyles} data-testid="Phone">
        <Box css={reviewTitleRowStyles}>
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles}>
              {!contactPhone && !isDisabled && (
                <AccordionIconError tabIndex={0} title="Phone incomplete error" marginRight="xs" />
              )}
            </Box>
            <Text textStyle="body-bold">Phone</Text>
          </Box>

          <ContentSectionEditButton
            buttonFieldName={ariaLabels.CONTACT_INFO_PHONE}
            value={SF_MODAL_COMPONENTS.PhoneNumberFormField.componentKey}
            onEditClick={handleEditPhoneClick}
          />
        </Box>

        <FieldValueDisplay value={displayContactPhone} className="fs-mask" />

        {displayContactPhone && !isDisabled && !isRoomForRent && (
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Box css={iconPlaceholderStyles} />
            <Box css={{ flex: '1 0' }}>
              <Box css={{ marginTop: '400' }}>
                <PhoneNumberDisplayInfo />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {forRentByType !== ForRentByType.AGENT && !isHidePhoneRestricted && (
        <Box css={reviewSectionStyles} data-testid="AllowContactByPhone">
          <Box css={reviewTitleRowStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Box css={iconPlaceholderStyles}>
                {recommendations[RECS.HIDE_PHONE] && (
                  <RecommendationIconBulb
                    fieldName={ariaLabels.CONTACT_BY_PHONE}
                    removeFlex={true}
                    onClick={() => {
                      setRecommendationType(RECS.HIDE_PHONE);
                    }}
                  />
                )}
              </Box>
              <Box css={reviewSectionTitleStyles}>
                <Text textStyle="body-bold">Allow renters to contact you by phone</Text>
                {recommendationTypes.has(RECS.HIDE_PHONE) && (
                  <TextButton
                    aria-label={`learn-more-about-${ariaLabels.CONTACT_BY_PHONE}`}
                    textStyle="body"
                    onClick={() => {
                      setRecommendationType(RECS.HIDE_PHONE);
                    }}
                  >
                    Learn more
                  </TextButton>
                )}
              </Box>
            </Box>

            <ContentSectionEditButton
              buttonFieldName={ariaLabels.CONTACT_BY_PHONE}
              value={SF_MODAL_COMPONENTS.AllowContactByPhoneFormField.componentKey}
              onEditClick={onEditClick}
            />
          </Box>

          <FieldValueDisplay value={typeCheckUtils.booleanToLabel(listingDetails?.hidePhone, 'No', 'Yes')} />
        </Box>
      )}

      {canAccessPostingPathTourScheduling && (
        <>
          <Divider />
          <Box css={{ marginBottom: '1000' }} />

          <div data-eventid={TOURING_EVENT_IDS.REVIEW_STEP_BOOKING_METHOD_IMPRESSION}>
            <TouringSection
              onClickRecommendation={setRecommendationType}
              onClickEditTourSection={handleEditTourClick}
            />
          </div>
        </>
      )}

      {isApplicationsAccessRestricted === false && (
        <>
          <Divider />
          <Box css={{ marginBottom: '1000' }} />

          <Box data-testid="ZillowApplications">
            <Box css={reviewTitleRowStyles}>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Box css={iconPlaceholderStyles}>
                  {!listingDetails?.active &&
                  !isApplicationsUpdateRestricted &&
                  (isApplicationsAccepted === null ||
                    (!isApplicationsAccepted && !typeCheckUtils.isNumeric(feeAmount))) &&
                  !isDisabled ? (
                    <AccordionIconError tabIndex={0} title="Applications incomplete error" marginRight="xs" />
                  ) : recommendations[RECS.APPLICATIONS] ? (
                    <RecommendationIconBulb
                      fieldName={ariaLabels.ZILLOW_APPLICATIONS}
                      removeFlex={true}
                      onClick={() => {
                        setRecommendationType(RECS.APPLICATIONS);
                      }}
                    />
                  ) : null}
                </Box>

                <Box css={reviewSectionTitleStyles}>
                  <Text textStyle="body-bold">Accept Zillow online applications</Text>

                  <Box css={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', height: '600' }}>
                    <Tag tone="info" css={{ marginRight: '200' }}>
                      Free
                    </Tag>

                    <TextButton
                      aria-label={`see-sample-${ariaLabels.ZILLOW_APPLICATIONS}`}
                      textStyle="body"
                      onClick={() => {
                        setDisplaySampleModal(true);

                        if (listingDetails && user) {
                          emitClickSampleApplicationLinkOnPageEvent({
                            currentProperty: listingDetails,
                            user,
                          });
                        }
                      }}
                      data-eventid={EVENT_IDS.CLICK_SAMPLE_APPLICATION_LINK_ON_PAGE}
                    >
                      Sample
                    </TextButton>
                  </Box>
                </Box>
              </Box>

              <ContentSectionEditButton
                buttonFieldName={ariaLabels.ZILLOW_APPLICATIONS}
                value={SF_MODAL_COMPONENTS.AcceptZillowApplicationsFormField.componentKey}
                onEditClick={onEditClick}
                allowFeedEdit={true}
                isDisabled={isApplicationsUpdateRestricted}
                disabledMessage={
                  applicationsOwnerEmail
                    ? `To make changes, log in as ${applicationsOwnerEmail}.`
                    : `To make changes, navigate to your Property Overview page, then select the Applications tab.`
                }
              />
            </Box>

            <FieldValueDisplay
              value={typeCheckUtils.booleanToYesNo(listingDetails?.isApplicationsAccepted)}
              customNoneText={!isApplicationsUpdateRestricted ? customNoneFieldValueText : 'None'}
            />

            {canAccessSFCostsAndFees && feeAmount !== null && !isApplicationsAccepted && (
              <Box css={{ marginTop: '400' }}>
                <FieldValueDisplay
                  value={`Application fee: ${numberUtils.asCurrency(feeAmount, feeAmount % 1 != 0 ? '$0,0.00' : '$0,0')}`}
                  customNoneText=""
                  showIconPlaceholder={true}
                />
              </Box>
            )}
          </Box>
        </>
      )}

      {showPhoneVerificationModal && (
        <VerificationModal
          headerText="Add and verify phone number"
          onContentValueAdded={handlePhoneNumberAdded}
          onVerificationConfirmation={handleVerificationConfirmation}
          showVerificationModal={setShowPhoneVerificationModal}
          stepsConfig={DEFAULT_VERIFICATION_CONFIG}
          verificationSource={PhoneVerificationSource.LISTING_CONTACT}
        />
      )}
      {recommendationType && (
        <RecommendationModal onClose={() => setRecommendationType('')} recommendationType={recommendationType} />
      )}
      {displaySampleModal && <ApplicationSampleModal onClose={() => setDisplaySampleModal(false)} />}
      {displayTourBookingModal && (
        <EditListingTourConfigModal
          step={touringModalStep}
          onStepChange={handleTouringModalStepChange}
          tourCalendarHook={tourCalendarApiHook}
          onClose={() => setDisplayTourBookingModal(false)}
          isInRelistingFlow={canAccessRelistingFlow}
          onTourDurationSave={handleTourDurationSave}
          onTourDurationCancel={handleTourDurationCancel}
          canAccessPostingPathTourDuration={true}
        />
      )}
    </div>
  );
};

export default FinalDetailsContent;
